import { processScenarioHistoryData } from "@wealthx/borrow-capacity-lib";
import { endOfMonth, format, startOfMonth, sub, subMonths } from "date-fns";
import { keyBy } from "lodash";
import { useContext, useMemo } from "react";
import { useMutation, useQueries, useQuery } from "react-query";
import {
  acceptOpenBankingAccessRequest,
  createScenario,
  deleteScenario,
  getIncomesAndExpensesSummaryByApplicant,
  getMyQuickLoan,
  getProductListByScenarioId,
  getScenario,
  getScenarioBankAccount,
  getScenarioFinanceHistory,
  sendOpenBankingAccessRequest,
  updateScenarioV2,
} from "../api/scenario";
import {
  ACCOUNT_CATEGORY,
  ACCOUNT_TYPE,
  FINANCE_HISTORY_DAY_FORMAT,
} from "../commons/enum";
import { FinanceContext } from "../context/finance-context";
import JointAccountContext from "../context/joint-account-context";
import { calculateLvr } from "../utils/scenario-utils";
import { useInstitutionList } from "./account.hooks";
import { useJointAccountFinanceHistory } from "./joint-account.hooks";
import { useIsLoggedIn, useMyInfo } from "./user.hooks.ts";
import { useQueryInvalidationWithNotification } from "./util.hooks";

export const DEFAULT_NUMBER_OF_MONTHS = 12;
export const DEFAULT_NUMBER_OF_LOOKBACK_MONTHS = 2;

export const useIsScenarioOwner = () => {
  const { data: userInfo = {} } = useMyInfo();
  const { selectedScenarioDetail } = useContext(FinanceContext);
  return selectedScenarioDetail?.mainUserId === userInfo?.id;
};

export const useScenarioList = (
  jointAccountId,
  options = { pending: false, retry: 2 }
) => {
  return useQuery(
    ["scenarioList", jointAccountId],
    () => getScenario(jointAccountId),
    {
      retry: options.retry,
      enabled: !options.pending,
    }
  );
};

export const useCreateScenarioMutation = () => {
  const { selectedJointAccount } = useContext(JointAccountContext) || {};
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useMutation(
    (params) =>
      createScenario({
        ...params,
        jointAccountId: selectedJointAccount || undefined,
      }),
    {
      onSuccess: () => {
        invalidateQueries("scenarioList");
      },
    }
  );
};

export const useCreateMultipleScenariosMutation = () => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useMutation(async (scenarios) => {
    const result = await Promise.allSettled(
      scenarios.map((scenario) => createScenario(scenario))
    );
    const isAllFailed = result.every((item) => item.status === "rejected");
    if (isAllFailed) {
      throw new Error("Failed to create scenarios");
    }
    invalidateQueries("scenarioList");
    return result
      .filter((item) => item.status === "fulfilled")
      .map((item) => item.value);
  });
};

export const useUpdateScenarioMutation = () => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useMutation(({ id, params }) => updateScenarioV2(id, params), {
    onSuccess: () => {
      invalidateQueries("scenarioList");
    },
  });
};

export const useScenarioProductListByScenarioId = (scenarioId) => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useQuery(
    ["scenarioProductList", scenarioId],
    () => getProductListByScenarioId(scenarioId),

    {
      onSuccess: () => {
        invalidateQueries("scenarioList");
      },
      enabled: !!scenarioId,
      retry: 3,
    }
  );
};

export const useLvr = (estimateProperty, cashTowardProperty) => {
  return useMemo(() => {
    return calculateLvr(estimateProperty, cashTowardProperty);
  }, [estimateProperty, cashTowardProperty]);
};

export const useGetMyQuickLoan = (options = {}) => {
  const isLoggedIn = useIsLoggedIn();
  return useQuery(
    "myQuickLoan",
    async () => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getMyQuickLoan());
        }, 2000);
      });
    },
    {
      enabled: !!isLoggedIn,
      ...options,
    }
  );
};

export const useScenarioBankAccount = (scenarioId, enabled = true) => {
  const isLoggedIn = useIsLoggedIn();

  return useQuery(
    ["scenarioBankAccount", scenarioId],
    () => getScenarioBankAccount(scenarioId),
    {
      enabled: !!isLoggedIn && !!scenarioId && enabled,
      refetchInterval: 5000, // Refetch every 5 seconds
      refetchIntervalInBackground: false, // Only refetch when tab is active
    }
  );
};

export const useCoApplicantUserIdInScenario = (scenarioId, applicantEmail) => {
  const { data: scenarioBankAccount } = useScenarioBankAccount(scenarioId);
  return useMemo(() => {
    if (!applicantEmail) return undefined;
    const bankAccountData = scenarioBankAccount?.find(
      (account) => account.user?.email === applicantEmail
    );
    if (bankAccountData?.isMain) return undefined;
    return bankAccountData?.user?.id;
  }, [scenarioBankAccount, applicantEmail]);
};

export const useListBankAccountByApplicant = (scenarioId, applicantId) => {
  const { data: scenarioBankAccount, isLoading: isScenarioBankAccountLoading } =
    useScenarioBankAccount(scenarioId);
  const { data: institutionList, isLoading: isInstitutionListLoading } =
    useInstitutionList();

  const institutionMap = useMemo(() => {
    const institutionListData = institutionList?.data || [];
    return keyBy(institutionListData, "id");
  }, [institutionList]);

  return useMemo(() => {
    if (
      isScenarioBankAccountLoading ||
      isInstitutionListLoading ||
      !scenarioBankAccount?.length
    )
      return [];
    const userAccount = scenarioBankAccount.find(
      (account) => account?.user?.id === applicantId
    );
    return (
      userAccount?.bankAccounts.map((bankAccount) => ({
        ...bankAccount,
        institutionDetail: institutionMap[bankAccount.institution],
        institutionLogoUrl: institutionMap[bankAccount.institution]?.logo,
      })) || []
    );
  }, [
    isScenarioBankAccountLoading,
    isInstitutionListLoading,
    scenarioBankAccount,
    applicantId,
    institutionMap,
  ]);
};

export const useCategorizedListBankAccountByApplicant = (
  scenarioId,
  applicantId
) => {
  const listBankAccountByApplicant = useListBankAccountByApplicant(
    scenarioId,
    applicantId
  );
  return useMemo(() => {
    const bankAccountCategories = {
      [ACCOUNT_CATEGORY.PROPERTY_LOAN]: [],
      [ACCOUNT_CATEGORY.LOAN]: [],
      [ACCOUNT_CATEGORY.CREDIT]: [],
      [ACCOUNT_CATEGORY.OTHERS]: [],
    };

    return listBankAccountByApplicant.reduce((acc, account) => {
      const type = account.class?.type;
      if (type === ACCOUNT_TYPE.MORTGAGE) {
        acc[ACCOUNT_CATEGORY.PROPERTY_LOAN].push(account);
      } else if (type === ACCOUNT_TYPE.LOAN) {
        acc[ACCOUNT_CATEGORY.LOAN].push(account);
      } else if (type === ACCOUNT_TYPE.CREDIT_CARD) {
        acc[ACCOUNT_CATEGORY.CREDIT].push(account);
      } else {
        acc[ACCOUNT_CATEGORY.OTHERS].push(account);
      }
      return acc;
    }, bankAccountCategories);
  }, [listBankAccountByApplicant]);
};

export const useIncomesAndExpensesSummaryByApplicantInLastXMonths = (
  scenarioId,
  { applicantUserId, numberOfMonths = 12, numberOfLookbackMonths = 3 }
) => {
  // * Eg: If today is in Feb 2025, then 12 months summary will start from 2024-02-01 to 2025-01-31
  const isLoggedIn = useIsLoggedIn();
  const endOfLastMonth = endOfMonth(sub(new Date(), { months: 1 }));
  const fromDate = startOfMonth(
    sub(endOfLastMonth, { months: numberOfMonths + numberOfLookbackMonths - 1 })
  );

  const to = format(endOfLastMonth, FINANCE_HISTORY_DAY_FORMAT);
  const from = format(fromDate, FINANCE_HISTORY_DAY_FORMAT);

  const options = {
    includeIncomeSources: false,
    includeIncomeByLastXMonths: true,
    includeExpenseCategories: false,
    includeConnectiveCategories: true,
    includeConnectiveExpenseByLastXMonths: false,
    includeDebtRepayment: true,
  };
  return useQuery(
    [
      "incomesAndExpensesSummaryByApplicant",
      scenarioId,
      applicantUserId,
      numberOfMonths,
      numberOfLookbackMonths,
    ],
    () =>
      getIncomesAndExpensesSummaryByApplicant(scenarioId, {
        applicantUserId,
        from,
        to,
        numberOfLookbackMonths,
        options,
      }),
    {
      enabled: isLoggedIn && !!scenarioId && !!applicantUserId,
    }
  );
};

export const useLastXMonthsScenarioFinanceSummary = (
  options = {
    numberOfMonths: DEFAULT_NUMBER_OF_MONTHS,
    numberOfLookbackMonths: DEFAULT_NUMBER_OF_LOOKBACK_MONTHS,
  }
) => {
  const { selectedJointAccount = "" } = useContext(JointAccountContext);
  const { numberOfMonths, numberOfLookbackMonths } = options;
  const endOfLastMonth = endOfMonth(sub(new Date(), { months: 1 }));
  const fromDate = startOfMonth(
    sub(endOfLastMonth, {
      months: numberOfMonths + numberOfLookbackMonths,
    })
  );
  const params = {
    from: format(fromDate, FINANCE_HISTORY_DAY_FORMAT),
    to: format(endOfLastMonth, FINANCE_HISTORY_DAY_FORMAT),
    numberOfLookbackMonths,
  };
  const userFinanceHistory = useQuery(["scenarioFinanceHistory", options], () =>
    getScenarioFinanceHistory(params)
  );
  const jointAccountFinanceHistory = useJointAccountFinanceHistory(
    selectedJointAccount,
    params
  );

  if (selectedJointAccount) {
    return jointAccountFinanceHistory;
  }
  return userFinanceHistory;
};

export const useLastXMonthsFinanceHistory = (
  options = {
    numberOfMonths: DEFAULT_NUMBER_OF_MONTHS,
    numberOfLookbackMonths: DEFAULT_NUMBER_OF_LOOKBACK_MONTHS,
  }
) => {
  const { numberOfMonths, numberOfLookbackMonths } = options;
  const { data: userInfo = {} } = useMyInfo();
  const { data: scenarioFinanceHistory, isLoading } =
    useLastXMonthsScenarioFinanceSummary({
      numberOfMonths,
      numberOfLookbackMonths,
    });
  if (isLoading) {
    return {
      data: [],
      isLoading: true,
    };
  }
  const prorcessedData = processScenarioHistoryData(
    scenarioFinanceHistory,
    userInfo.id
  );
  return {
    data: prorcessedData,
    isLoading: false,
  };
};

export const useLastMonthFinanceHistory = () => {
  const { data: scenarioFinanceHistory, isLoading } =
    useLastXMonthsFinanceHistory({
      numberOfMonths: 0,
      numberOfLookbackMonths: DEFAULT_NUMBER_OF_LOOKBACK_MONTHS,
    });

  const lastMonthKey = useMemo(
    () => format(subMonths(new Date(), 1), "yyyy-MM"),
    []
  );

  if (isLoading) {
    return {
      data: null,
      isLoading: true,
    };
  }

  return {
    data:
      scenarioFinanceHistory.find((item) => item.month === lastMonthKey) || {},
    isLoading: false,
  };
};

export const useDeleteScenario = () => {
  const invalidateQueries = useQueryInvalidationWithNotification();
  return useMutation((id) => deleteScenario(id), {
    onSuccess: () => {
      invalidateQueries("scenarioList");
    },
  });
};

export const useSendOpenBankingAccessRequest = () => {
  return useMutation((payload) => sendOpenBankingAccessRequest(payload));
};

export const useAcceptOpenBankingAccessRequest = () => {
  return useMutation(({ token }) => acceptOpenBankingAccessRequest(token));
};

export const useAllApplicantsExpenseData = (
  scenarioId,
  applicants,
  options = {}
) => {
  const { numberOfMonths = 12, numberOfLookbackMonths = 3 } = options;
  const isLoggedIn = useIsLoggedIn();

  // Get scenario bank accounts to find applicant user IDs
  const { data: scenarioBankAccount, isLoading: isScenarioBankAccountLoading } =
    useScenarioBankAccount(scenarioId);

  // Prepare data for queries
  const endOfLastMonth = endOfMonth(sub(new Date(), { months: 1 }));
  const fromDate = startOfMonth(
    sub(endOfLastMonth, { months: numberOfMonths + numberOfLookbackMonths - 1 })
  );
  const to = format(endOfLastMonth, FINANCE_HISTORY_DAY_FORMAT);
  const from = format(fromDate, FINANCE_HISTORY_DAY_FORMAT);

  const queryOptions = {
    includeIncomeSources: false,
    includeIncomeByLastXMonths: true,
    includeExpenseCategories: false,
    includeConnectiveCategories: true,
    includeConnectiveExpenseByLastXMonths: false,
    includeDebtRepayment: true,
  };

  // Create queries for each applicant
  const queries = applicants.map((applicant) => {
    const userAccount = scenarioBankAccount?.find(
      applicant?.email
        ? (account) => account?.user?.email === applicant?.email
        : (account) => account.isMain
    );
    const applicantUserId = userAccount?.user?.id;
    return {
      queryKey: [
        "incomesAndExpensesSummaryByApplicant",
        scenarioId,
        applicantUserId,
        numberOfMonths,
        numberOfLookbackMonths,
      ],
      queryFn: () => {
        return getIncomesAndExpensesSummaryByApplicant(scenarioId, {
          applicantUserId,
          from,
          to,
          numberOfLookbackMonths,
          options: queryOptions,
        });
      },
      enabled:
        isLoggedIn &&
        !!scenarioId &&
        !!applicantUserId &&
        !isScenarioBankAccountLoading,
      retry: 0,
    };
  });

  return useQueries(queries);
};
